import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle 401 by trying refresh, then redirect to login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      const refreshToken = localStorage.getItem('refreshToken')
      console.log('[API] 401 intercepted, refreshToken:', refreshToken ? 'present' : 'missing')

      if (refreshToken) {
        try {
          const res = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
            refresh_token: refreshToken,
          })
          const { access_token, refresh_token } = res.data
          console.log('[API] Refresh successful, new token stored')
          localStorage.setItem('token', access_token)
          if (refresh_token) {
            localStorage.setItem('refreshToken', refresh_token)
          }
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        } catch (refreshErr: any) {
          console.error('[API] Refresh failed:', refreshErr.response?.status, refreshErr.response?.data)
          // Refresh failed, clear auth and redirect
          localStorage.removeItem('token')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
          window.location.href = '/login'
        }
      } else {
        console.error('[API] No refresh token, redirecting to login')
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Auth API helpers
export async function login(email: string, password: string) {
  const res = await api.post('/api/v1/auth/login', { email, password })
  return res.data
}

export async function register(name: string, email: string, password: string) {
  const res = await api.post('/api/v1/auth/register', { name, email, password })
  return res.data
}

export async function forgotPassword(email: string) {
  const res = await api.post('/api/v1/auth/forgot-password', { email })
  return res.data as { detail: string }
}

export async function resetPassword(token: string, password: string) {
  const res = await api.post('/api/v1/auth/reset-password', { token, password })
  return res.data as { detail: string }
}

export async function getMe() {
  const res = await api.get('/api/v1/users/me')
  return res.data
}

export async function logout() {
  await api.post('/api/v1/auth/logout')
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
  window.location.href = '/login'
}
